import { createSelector } from "reselect"
import { safeDivide } from "helpers/math"
import { get, first, chain, find, kebabCase } from "lodash"
import { percentageFormatter, numberFormatter } from "helpers/formatters/numbers"
import { getSelectedGroup } from "./metrics"
import { getBenchmarkReturn, getForecast } from "./data"
import { getItems } from "./items"
import { buildGrossMarketValueRow } from "./gross-market-value-row"
import { addCompositionTopXRow } from "helpers/analyze"
import { UNKNOWN } from "helpers/data-statuses"

const EMPTY_OBJECT = {}
const getSelectedDate = (_, props) => props.frontierDate
const getPositionSet0 = (_, props) => props.positionSets[0]
const getPositionSet1 = (_, props) => props.positionSets[1]
const getPositionSet2 = (_, props) => props.positionSets[2]
const getPositionSet3 = (_, props) => props.positionSets[3]
const getPositionSet4 = (_, props) => props.positionSets[4]
export const getCurrency = (_, props) => props.currency
export const getFocusedLegendItem = (state) => get(state, "modules.compareFrontier.focusedLegendItem", EMPTY_OBJECT)
export const getHoveredLegendItem = (state) => get(state, "modules.compareFrontier.hoveredLegendItem", EMPTY_OBJECT)
export const getFrontierSettingsPanelOpen = (state) => get(state, "modules.compareFrontier.frontierSettingsPanelOpen")
const getData0 = state => get(state, "global.data.compareFrontierPositionSet0.data.model", EMPTY_OBJECT)
const getData1 = state => get(state, "global.data.compareFrontierPositionSet1.data.model", EMPTY_OBJECT)
const getData2 = state => get(state, "global.data.compareFrontierPositionSet2.data.model", EMPTY_OBJECT)
const getData3 = state => get(state, "global.data.compareFrontierPositionSet3.data.model", EMPTY_OBJECT)
const getData4 = state => get(state, "global.data.compareFrontierPositionSet4.data.model", EMPTY_OBJECT)

const getDataStatus0 = state => get(state, "global.data.compareFrontierPositionSet0.status", UNKNOWN)
const getDataStatus1 = state => get(state, "global.data.compareFrontierPositionSet1.status", UNKNOWN)
const getDataStatus2 = state => get(state, "global.data.compareFrontierPositionSet2.status", UNKNOWN)
const getDataStatus3 = state => get(state, "global.data.compareFrontierPositionSet3.status", UNKNOWN)
const getDataStatus4 = state => get(state, "global.data.compareFrontierPositionSet4.status", UNKNOWN)

function computeForecast(summary, forecast, optimizedPositions, portfolioEquity, benchmarkReturn) {
  if (optimizedPositions) {
    const totalRisk = get(summary, "total", 0)
    const expectedEquitiesReturns = get(forecast, "equities")
    const expectedSwapsReturns = get(forecast, "swaps")
    const returnPerc = chain(optimizedPositions)
      .sumBy(position => {
        let positionReturn

        switch (position.assetClass) {
          case "equity":
            positionReturn = find(expectedEquitiesReturns, erEquity => position.id === `${erEquity.id.ticker}.${erEquity.id.mic}`)
            break
          default:
            positionReturn = find(expectedSwapsReturns, erEquity => erEquity.id === position.id)
            break
        }

        if (positionReturn) {
          return positionReturn.expectedPercentReturn * safeDivide(position.economicExposure, portfolioEquity)
        } else {
          return 0
        }
      })
      .value()

    const performance = returnPerc - benchmarkReturn

    return {
      performance,
      risk: totalRisk * 100,
      sharpe: safeDivide(performance, totalRisk * 100),
    }
  }

  return EMPTY_OBJECT
}

function getDailyData(status, selectedDate, data, selectedGroup, impliedReturn, benchmarkReturn, positionSet) {
  if (!data.simulation || !data.compositionSimulation || !data.compositionSimulation.composition || !data.simulation.risk) return null

  const compositionData = first(data.compositionSimulation.composition)
  const riskData = first(data.simulation.risk)
  const compositionDataNonActive = first(data.compositionNonActive.composition)

  if (!compositionData || !riskData || !impliedReturn || !positionSet) {
    return null
  }

  let compositionBy
  const compositionByData = selectedGroup.id === "LONG_SHORT" ? compositionData.longShortComposition : compositionData.compositionBy

  if (selectedGroup.id === "LONG_SHORT") {
    if (compositionByData.length > 1) {
      const longShort = chain(compositionByData)
        .mapKeys("id")
        .mapValues(item => Math.abs(item.economicExposure))
        .value()

      compositionBy = [
        { label: "Long", value: longShort.LONG },
        { label: "Short", value: longShort.SHORT },
        { label: "Net", value: longShort.LONG - longShort.SHORT },
      ]
    } else {
      const data = compositionByData[0]
      compositionBy = [{
        label: data.id === "LONG" ? "Long Only" : "Short Only",
        value: Math.abs(data.economicExposure),
      }]
    }
  } else {
    compositionBy = chain(compositionByData)
      .map(item => ({ label: item.description, value: item.economicExposure }))
      .orderBy([item => Math.abs(item.value)], ["desc"])
      .value()
  }

  addCompositionTopXRow(compositionBy, selectedGroup.label)

  const forecast = computeForecast(
    riskData,
    impliedReturn,
    compositionDataNonActive.positions,
    compositionDataNonActive.referenceEquity || compositionDataNonActive.modeledGmv,
    benchmarkReturn,
  )

  return {
    loading: status,
    summary: {
      gmv: compositionData.gmv,
      positionsCount: compositionData.positionsCount,
      minConcentration: compositionData.concentration.min,
      maxConcentration: compositionData.concentration.max,
    },
    risk: {
      specific: riskData.attribution.summary.specific * 100,
      factors: riskData.attribution.summary.factors * 100,
    },
    forecast,
    groups: chain(compositionBy)
      .map(item => {
        const absValue = Math.abs(item.value)
        return {
          ...item,
          chartWidth: percentageFormatter(absValue < 100 ? absValue : 100, true),
        }
      })
      .value(),
  }
}

const getDailyData0 = createSelector([getDataStatus0, getSelectedDate, getData0, getSelectedGroup, getForecast, getBenchmarkReturn, getPositionSet0], getDailyData)
const getDailyData1 = createSelector([getDataStatus1, getSelectedDate, getData1, getSelectedGroup, getForecast, getBenchmarkReturn, getPositionSet1], getDailyData)
const getDailyData2 = createSelector([getDataStatus2, getSelectedDate, getData2, getSelectedGroup, getForecast, getBenchmarkReturn, getPositionSet2], getDailyData)
const getDailyData3 = createSelector([getDataStatus3, getSelectedDate, getData3, getSelectedGroup, getForecast, getBenchmarkReturn, getPositionSet3], getDailyData)
const getDailyData4 = createSelector([getDataStatus4, getSelectedDate, getData4, getSelectedGroup, getForecast, getBenchmarkReturn, getPositionSet4], getDailyData)

export const getDetailsData = createSelector(
  [getDailyData0, getDailyData1, getDailyData2, getDailyData3, getDailyData4],
  (data0, data1, data2, data3, data4) => ([data0, data1, data2, data3, data4]),
)

const getDetailsStatuses = createSelector(
  [getDataStatus0, getDataStatus1, getDataStatus2, getDataStatus3, getDataStatus4],
  (status0, status1, status2, status3, status4) => ([status0, status1, status2, status3, status4]),
)

export const getDetailsItems = createSelector(
  [getDetailsData, getDetailsStatuses, getCurrency, getItems],
  (detailsData, statuses, currency, columns) => {
    return ({
      summary: [buildGrossMarketValueRow({ columns, detailsData, statuses, currency }), {
        label: "Positions",
        fn: item => get(item, "summary.positionsCount", 0),
      }, {
        label: "Concentration",
        fn: item => `${numberFormatter(get(item, "summary.minConcentration", 0))} - ${percentageFormatter(get(item, "summary.maxConcentration", 0), true)}`,
      }],
      risk: [{
        label: "Specific",
        fn: item => numberFormatter(get(item, "risk.specific", 0)),
      }, {
        label: "Factors",
        fn: item => numberFormatter(get(item, "risk.factors", 0)),
      }],
      forecast: {
        label: "Performance (%)",
        defaultOpen: true,
        fn: item => numberFormatter(get(item, "forecast.performance", 0)),
        items: [{
          label: "Risk",
          fn: item => numberFormatter(get(item, "forecast.risk", 0)),
        }, {
          label: "Sharpe",
          fn: item => numberFormatter(get(item, "forecast.sharpe", 0)),
        }],
      },
      groups: chain(detailsData)
        .map(item => item ? item.groups : null)
        .value(),
    })
  },
)

export const getChartDots = createSelector(
  [getDetailsData, getItems, getFocusedLegendItem],
  (data, items, focusedItem) =>
    chain(data)
      .map((datum, i) => {
        if (datum) {
          const item = items[i]

          if (item) {
            return {
              id: item.id,
              className: kebabCase(item.label),
              focused: focusedItem && focusedItem.id === item.id,
              x: datum.forecast.risk,
              y: datum.forecast.performance,
              color: item.color,
              label: item.label,
            }
          }
        }

        return null
      })
      .filter(datum => datum)
      .orderBy(dot => dot.focused)
      .value(),
)
